import { DeleteIcon, InfoIcon, SmallAddIcon } from '@chakra-ui/icons';
import { Box, Button, Checkbox, Flex, IconButton, Text, Tooltip, VStack } from '@chakra-ui/react';
import { ColumnDef } from '@tanstack/react-table';
import { kebabCase } from 'lodash';
import { useCallback, useMemo } from 'react';
import { Controller, useFieldArray, UseFormReturn } from 'react-hook-form';

import { GasComposition, initGasComposition } from '@/apis/makgeolli/factors/greenhouse-gases.dto';
import { FormMode } from '@/app/org-cfv/src/enums/EmissionSourceEnum';
import SortableTable from '@/components/common/DataDisplay/table/SortableTable/SortableTable';
import Label from '@/components/common/Forms/inputs/Label/Label';
import TextInput from '@/components/common/Forms/inputs/TextInput/TextInput';
import { IoInformationCircle } from '@/components/common/Icons/IoInformationCircle';
import { useNextTranslation } from '@/i18n/client';

import { RefrigerantSettingSchemaType } from './RefrigerantSettingModal';

type ColumnType = ColumnDef<Pick<GasComposition, 'shortName' | 'ratio' | 'isIncluded'>>[];
type Props = {
  methods: UseFormReturn<RefrigerantSettingSchemaType, unknown, undefined>;
  mode: FormMode;
  totalRatio: number | undefined;
};
const RefrigerantCustomBlock = ({ methods, mode, totalRatio = 0 }: Props) => {
  const { t } = useNextTranslation();

  const { fields, append, remove } = useFieldArray<RefrigerantSettingSchemaType>({
    control: methods.control,
    name: 'gasComposition',
  });
  const curRefrigerant = methods.watch();

  const handleDeleteGas = useCallback(
    (targetIndex: number) => {
      remove(targetIndex);
    },
    [remove],
  );

  const handleAddNewGas = () => {
    append(initGasComposition as Required<GasComposition>);
  };

  const customColumns = useMemo(() => {
    const baseColumns: ColumnType = [
      {
        header: t('common.gasName'),
        id: kebabCase('shortName'),
        accessorKey: 'shortName',
        size: 282,
        cell: ({ getValue, row: { index } }) => {
          return (
            <Controller
              name={`gasComposition.${index}.shortName`}
              control={methods.control}
              render={({ field, fieldState: { error } }) =>
                mode === FormMode.VIEW ? (
                  <Text whiteSpace={'break-spaces'}>{String(getValue())}</Text>
                ) : (
                  <TextInput
                    required
                    type='text'
                    data-testid={kebabCase('gasCompositionShortName')}
                    autoComplete='off'
                    errorMessageType='tooltip'
                    errorMessage={error?.message || undefined}
                    {...field}
                  />
                )
              }
            />
          );
        },
      },
      {
        header: `GWP ${t('common.percentage')}`,
        id: kebabCase('ratio'),
        accessorKey: 'ratio',
        meta: { align: 'right' },
        cell: ({ getValue, row: { index } }) =>
          mode === FormMode.VIEW ? (
            <Text whiteSpace={'break-spaces'}>{String(getValue())}</Text>
          ) : (
            <Controller
              name={`gasComposition.${index}.ratio`}
              control={methods.control}
              render={({ field: { value, onChange, ...field }, fieldState: { error } }) => (
                <TextInput
                  textAlign={'right'}
                  required
                  value={value || ''}
                  type='number'
                  min={0}
                  max={100}
                  data-testid='ratio'
                  autoComplete='off'
                  errorMessageType='tooltip'
                  errorMessage={error?.message || undefined}
                  onChange={(e) => onChange(Number(e.target.value))}
                  {...field}
                />
              )}
            />
          ),
      },
      {
        header: t('common.includeInEmissionCalc'),
        id: kebabCase('isIncluded'),
        accessorKey: 'isIncluded',
        meta: { align: 'center' },
        cell: ({ row: { index } }) => (
          <Controller
            name={`gasComposition.${index}.isIncluded`}
            control={methods.control}
            render={({ field: { value, ...field } }) => (
              <Checkbox size='lg' isChecked={value} isDisabled={mode === FormMode.VIEW} {...field} />
            )}
          />
        ),
      },
    ];
    const actionCol: ColumnType = [
      {
        header: t('common.action'),
        id: kebabCase('action'),
        accessorKey: 'action',
        meta: { align: 'center' },
        cell: ({ row: { index } }) => (
          <Tooltip hasArrow bg='gray.900' placement='bottom-start' fontSize='0.875rem' label={t('common.delete')}>
            <IconButton
              data-testid={'delete-gas'}
              size='sm'
              colorScheme={'red'}
              variant='outline'
              aria-label={'Delete Gas'}
              icon={<DeleteIcon />}
              isDisabled={fields.length === 1}
              onClick={() => handleDeleteGas(index)}
            />
          </Tooltip>
        ),
      },
    ];

    if ([FormMode.EDIT, FormMode.CREATE].includes(mode)) {
      return baseColumns.concat(actionCol) as ColumnType;
    }
    return baseColumns;
  }, [fields.length, handleDeleteGas, methods.control, mode, t]);

  return (
    <>
      <VStack gap={1} alignItems={'flex-start'} mb={4}>
        {mode === FormMode.VIEW ? (
          <Text fontSize='md' fontWeight='bold' lineHeight={1.2}>
            {t(`fugitive-emission.${curRefrigerant.shortName || ''}`)}
          </Text>
        ) : (
          <Controller
            name='shortName'
            control={methods.control}
            render={({ field, fieldState: { error } }) => (
              <TextInput
                required
                label={t('common.refrigerantName')}
                type='text'
                data-testid={kebabCase('refrigerantName')}
                autoComplete='off'
                width={'20rem'}
                errorMessage={error?.message || undefined}
                {...field}
              />
            )}
          />
        )}

        {mode === FormMode.VIEW ? (
          <Box display={'flex'} alignItems={'center'} fontSize='sm' lineHeight={1.5} color='blue.500'>
            <Text>{`GWP`}</Text>
            <Tooltip
              label={t('common.customGwpInEmissionCalc')}
              placement='bottom'
              hasArrow
              textAlign='center'
              backgroundColor='gray.900'
              maxWidth='60'
              width='auto'
            >
              <IoInformationCircle ml={1} />
            </Tooltip>
            <Text>{`：${curRefrigerant.gwp}`}</Text>
          </Box>
        ) : (
          <>
            <Label
              showTooltipIcon={true}
              label={'GWP'}
              tooltipLabel={t('common.customGwpInEmissionCalc')}
              tooltipProps={{ width: '12.5rem', borderRadius: '4px', padding: '0.125rem 0.5rem' }}
            />
            <Controller
              name='gwp'
              control={methods.control}
              render={({ field: { value, ...field }, fieldState: { error } }) => (
                <TextInput
                  required
                  type='number'
                  value={value || ''}
                  min={0}
                  data-testid={kebabCase('gwp')}
                  autoComplete='off'
                  width={'20rem'}
                  errorMessage={error?.message || undefined}
                  {...field}
                />
              )}
            />
          </>
        )}
      </VStack>
      <SortableTable
        testId='export-report-table'
        data={fields}
        columns={customColumns}
        isTableWidthAuto
        getRowCanExpand={() => false}
        footerContent={
          [FormMode.EDIT, FormMode.CREATE].includes(mode) && (
            <Flex flexDirection={'column'}>
              <Text fontSize={'sm'} fontWeight={'medium'} color={'blue.500'}>
                {t('common.totalGWPratio')}： {totalRatio} %
              </Text>
              {fields.length >= 5 && (
                <Flex gap={1} alignItems='center' mt={2}>
                  <InfoIcon fontSize={20} />
                  <Text color='black' fontSize={'sm'}>
                    {t('common.maximumQuantity')}
                  </Text>
                </Flex>
              )}
            </Flex>
          )
        }
        tableCaption={
          [FormMode.EDIT, FormMode.CREATE].includes(mode) &&
          fields.length < 5 && (
            <Button
              data-testid='add-new-gas-icon'
              variant='link'
              color='blue.500'
              leftIcon={<SmallAddIcon />}
              onClick={handleAddNewGas}
            >
              {t('common.add')}
            </Button>
          )
        }
        sx={{
          table: {
            minWidth: '100%',
          },
        }}
      />
    </>
  );
};

export default RefrigerantCustomBlock;
